import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api from "../api";
import { Building2 } from "lucide-react";
import TenantUnavailable from "../components/TenantUnavailable";
import InstallPWACard from "../components/InstallPWACard";
import StalePWABanner from "../components/StalePWABanner";
import { PLATFORM_NAME, PLATFORM_DEVELOPER } from "../lib/platform";

export default function Login() {
  const { login, tenant: authTenant } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const { tenantSlug } = useParams();
  // Pré-preenche o e-mail se veio do fluxo /entrar (via router state).
  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [tenant, setTenant] = useState(
    // Reaproveita o tenant já disponível no AuthContext (caso a página seja
    // acessada logado ou a discovery já tenha rodado) — evita duplicação de
    // fetch com o DocumentTitle e reduz race conditions em iOS Safari.
    authTenant && (!tenantSlug || authTenant.slug === tenantSlug) ? authTenant : null,
  );

  useEffect(() => {
    if (!tenantSlug) return;
    // Se já temos o tenant do AuthContext para este slug, não refaz o fetch.
    if (authTenant && authTenant.slug === tenantSlug) {
      setTenant(authTenant);
      return;
    }
    let cancelled = false;
    api.get(`/tenants/${tenantSlug}/public`)
      .then((r) => { if (!cancelled) setTenant(r.data); })
      .catch(() => { if (!cancelled) setTenant(null); });
    return () => { cancelled = true; };
  }, [tenantSlug, authTenant]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success(`Olá, ${(res.user.name || "").split(" ")[0]}!`);
      const slug = (res.tenant && res.tenant.slug) || tenantSlug || "";
      const home = res.user.role === "admin" ? "fila" : "minha-vez";
      nav(`/${slug}/${home}`);
    } else {
      toast.error(res.error || "Não foi possível entrar");
    }
  };

  const displayName = tenant?.display_name || tenant?.name || tenantSlug;
  const logoSrc = tenant?.logo_url || "/logo.png";
  const segment = tenant?.segment;
  const currentYear = new Date().getFullYear();

  if (tenant && tenant.active === false) {
    return (
      <div className="min-h-screen bg-grain flex flex-col items-center justify-center px-4 py-10">
        <TenantUnavailable tenant={tenant} testidPrefix="login-inactive" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-grain flex flex-col">
      <StalePWABanner />
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="mx-auto grid w-full max-w-5xl gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-center">
          {/* Coluna principal — identidade do tenant + formulário */}
          <div className="w-full max-w-md mx-auto lg:mx-0">
            <div className="mb-8 flex flex-col items-center gap-3 text-center">
              {tenant ? (
                <img
                  src={logoSrc}
                  alt={displayName}
                  data-testid="login-logo"
                  className="h-24 w-24 sm:h-28 sm:w-28 object-contain rounded-full border-2 border-brand-100 shadow-sm bg-white"
                  onError={(e) => {
                    if (e.currentTarget.dataset.fbk !== "1") {
                      e.currentTarget.dataset.fbk = "1";
                      e.currentTarget.src = "/logo.png";
                    }
                  }}
                />
              ) : (
                <div className="flex h-24 w-24 sm:h-28 sm:w-28 items-center justify-center rounded-full border-2 border-brand-100 bg-white text-gray-400">
                  <Building2 size={32} />
                </div>
              )}
              <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight" data-testid="login-tenant-display-name">
                {displayName}
              </h1>
              {segment && (
                <p className="text-[11px] uppercase tracking-[0.3em] text-brand-600 font-semibold" data-testid="login-tenant-segment">
                  {segment}
                </p>
              )}
            </div>

            <form
              onSubmit={onSubmit}
              data-testid="login-form"
              className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
            >
              <div className="space-y-5">
                <div>
                  <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">
                    Usuário ou e-mail
                  </label>
                  <input
                    data-testid="login-email-input"
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-base outline-none transition-colors focus:border-black"
                    placeholder="seu@email.com"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">
                    Senha
                  </label>
                  <input
                    data-testid="login-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-base outline-none transition-colors focus:border-black"
                    placeholder="••••••"
                    autoComplete="current-password"
                  />
                </div>
                <button
                  data-testid="login-submit-button"
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-full bg-black py-3.5 text-sm font-semibold uppercase tracking-widest text-white transition-transform hover:-translate-y-[1px] disabled:opacity-50"
                >
                  {busy ? "Entrando…" : `Entrar${tenant ? ` em ${displayName}` : ""}`}
                </button>
              </div>
            </form>

            {/* Convite discreto para instalar o PWA — mostra apenas dentro do tenant */}
            {tenantSlug && (
              <InstallPWACard slug={tenantSlug} tenantName={displayName} />
            )}
          </div>

          {/* Coluna lateral — preparada para futuros comunicados/anúncios da plataforma.
              Mantida vazia agora; oculta em telas < lg. */}
          <aside
            data-testid="login-side-slot"
            aria-hidden="true"
            className="hidden lg:block"
          />
        </div>
      </main>

      <footer className="border-t border-gray-100 px-4 py-4 text-center">
        <p className="text-[11px] uppercase tracking-[0.25em] text-gray-400" data-testid="login-footer">
          <span className="text-brand-500 font-semibold">{PLATFORM_NAME}</span> · Desenvolvido por <span className="font-semibold text-gray-600">{PLATFORM_DEVELOPER}</span> · © {currentYear}
        </p>
      </footer>
    </div>
  );
}
